import { foldName } from '../engine/fold.js';
/** Canonical and alternate tokens, by form. Token texts are folded (`foldName`). */
export const ALTERNATES = [
    { form: 'approximate', canonical: 'c.', alternates: ['c', 'circa', 'about', 'around', '~'] },
    { form: 'uncertain', canonical: '?', alternates: [] },
    { form: 'range', canonical: '–', alternates: ['-', '—', 'to'] },
    { form: 'open_lo', canonical: 'until', alternates: [] },
    { form: 'open_hi', canonical: 'since', alternates: [] },
    { form: 'time', canonical: ',', alternates: ['', 'at', ', at'] },
    { form: 'hour', canonical: '20h', alternates: [] },
    { form: 'century', canonical: '11th century', alternates: ['century 1000'] },
    { form: 'era_of_date', canonical: 'suffix after', alternates: ['name before', 'name after'] },
    { form: 'now_offset', canonical: 'now + 2 days', alternates: ['now+2d', 'now +2 days'] },
    { form: 'minute', canonical: 'minute', alternates: ['minutes', 'min', 'mins', 'm'] },
    { form: 'hour_unit', canonical: 'hour', alternates: ['hours', 'hr', 'hrs', 'h'] },
    { form: 'day', canonical: 'day', alternates: ['days', 'd'] },
    { form: 'week', canonical: 'week', alternates: ['weeks', 'wk', 'w'] },
    { form: 'month', canonical: 'month', alternates: ['months', 'mo'] },
    { form: 'year', canonical: 'year', alternates: ['years', 'yr', 'y'] },
    { form: 'lag_range', canonical: '3–5 days', alternates: ['3-5 days', '3 to 5 days'] },
    { form: 'anchor', canonical: '@[Label](ref:fid)', alternates: ['@[Label]', '@Label'] },
];
function row(form) {
    const found = ALTERNATES.find((r) => r.form === form);
    return [found.canonical, ...found.alternates];
}
/** One-token words of "approximate" (`c.` is read as `c` with an optional `.`). */
export const APPROX_WORDS = new Set(row('approximate').map((w) => foldName(w).replace(/\.$/, '')));
/** Range separators after folding: every dash folds to `-`. */
export const RANGE_WORDS = new Set(row('range').map(foldName));
const UNIT_FORMS = {
    minute: 'minute',
    hour: 'hour_unit',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
};
/** Every word of a unit (now offsets and lags), folded, to its `TimeUnit`. */
export const UNIT_WORDS = new Map(Object.keys(UNIT_FORMS).flatMap((unit) => row(UNIT_FORMS[unit]).map((w) => [w, unit])));
/** The written unit: singular for 1, plural otherwise. */
export function unitText(unit, n) {
    return n === 1 ? unit : `${unit}s`;
}
export const PART_WORDS = new Set(['early', 'mid', 'late']);
/** Words the grammar gives a meaning; a leftover keyword is `unexpected_text`, not a name. */
export const KEYWORDS = new Set([
    'now',
    'session',
    'undated',
    'early',
    'mid',
    'late',
    'week',
    'of',
    'century',
    'until',
    'since',
    'to',
    'at',
    'after',
    'before',
    'during',
    'same',
    'time',
    'as',
    'least',
    'within',
    ...APPROX_WORDS,
]);
const WORD = /[\p{L}\p{M}\p{N}_'’]+/uy;
const SPACE = /\s+/uy;
/** Reads `@[Label](ref:<fid>)`, `@[Label]` or `@Word` from `input` at `at` (the `@`). */
function readAnchor(input, at) {
    let i = at + 1;
    if (input[i] === '[') {
        const close = input.indexOf(']', i + 1);
        if (close < 0)
            return { kind: 'anchor', label: null, fid: null, at, end: input.length };
        const label = input.slice(i + 1, close).trim();
        i = close + 1;
        let fid = null;
        if (input.startsWith('(ref:', i)) {
            const end = input.indexOf(')', i);
            const value = end < 0 ? '' : input.slice(i + 5, end).trim();
            if (value === '')
                return { kind: 'anchor', label: null, fid: null, at, end: input.length };
            fid = value;
            i = end + 1;
        }
        return { kind: 'anchor', label: label === '' ? null : label, fid, at, end: i };
    }
    WORD.lastIndex = i;
    const m = WORD.exec(input);
    if (m === null)
        return { kind: 'anchor', label: null, fid: null, at, end: i };
    return { kind: 'anchor', label: m[0], fid: null, at, end: i + m[0].length };
}
/**
 * Splits `input` into words (letters, marks, digits, `_` and apostrophes), single punctuation
 * characters and anchors, each with its offset in `input`. Word and punctuation texts are folded.
 */
export function tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        SPACE.lastIndex = i;
        const space = SPACE.exec(input);
        if (space !== null) {
            i += space[0].length;
            continue;
        }
        WORD.lastIndex = i;
        const word = WORD.exec(input);
        if (word !== null) {
            tokens.push({ kind: 'word', text: foldName(word[0]), at: i, end: i + word[0].length });
            i += word[0].length;
            continue;
        }
        const cp = input.codePointAt(i);
        const ch = String.fromCodePoint(cp);
        if (ch === '@') {
            const anchor = readAnchor(input, i);
            tokens.push(anchor);
            i = anchor.end;
            continue;
        }
        tokens.push({ kind: 'punct', text: foldName(ch), at: i, end: i + ch.length });
        i += ch.length;
    }
    return tokens;
}
/** The folded word texts of a name, as the tokenizer splits it (empty for an empty name). */
export function nameTokens(name) {
    return tokenize(name).map((t) => (t.kind === 'anchor' ? '@' : t.text));
}
//# sourceMappingURL=grammar.js.map